import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import RequireAuth from './components/RequireAuth';

// Route-level chunks keep the shell/login small and defer the large connection, governance, and
// package-management workspaces until the operator actually opens them.  This replaces a single
// 1.40 MB production entry chunk that every screen paid for up front.
const Login = lazy(() => import('./pages/Login'));
const PlatformDashboard = lazy(() => import('./pages/PlatformDashboard'));
const Connections = lazy(() => import('./pages/Connections'));
const ConnectionWizard = lazy(() => import('./pages/ConnectionWizard'));
const ConnectionWorkspace = lazy(() => import('./pages/ConnectionWorkspace'));
const ApiConnectionWizard = lazy(() => import('./pages/ApiConnectionWizard'));
const Platform = lazy(() => import('./pages/Platform'));
const EnvironmentManagement = lazy(() => import('./pages/EnvironmentManagement'));
const LicenseManagement = lazy(() => import('./pages/LicenseManagement'));
const Packages = lazy(() => import('./pages/Packages'));
const RuntimeManager = lazy(() => import('./pages/RuntimeManager'));
const ImportWizard = lazy(() => import('./pages/ImportWizard'));
const ApiConnectionWorkspace = lazy(() => import('./pages/ApiConnectionWorkspace'));
const DataPlatformWizard = lazy(() => import('./pages/DataPlatformWizard'));
const FileObjectWizard = lazy(() => import('./pages/FileObjectWizard'));
const StreamingWizard = lazy(() => import('./pages/StreamingWizard'));
const AuditTrail = lazy(() => import('./pages/AuditTrail'));
const Drift = lazy(() => import('./pages/Drift'));
const CatalogConnectionWizard = lazy(() => import('./pages/CatalogConnectionWizard'));
const McpServerWizard = lazy(() => import('./pages/McpServerWizard'));
const Usage = lazy(() => import('./pages/Usage'));
const ApiAccessUsers = lazy(() => import('./pages/ApiAccessUsers'));
const MyApiAccess = lazy(() => import('./pages/MyApiAccess'));
const ApiAccessRoles = lazy(() => import('./pages/ApiAccessRoles'));
const AuthzResources = lazy(() => import('./pages/AuthzResources'));
const AuthzActivity = lazy(() => import('./pages/AuthzActivity'));
const PolicyManagement = lazy(() => import('./pages/PolicyManagement'));
const AuthzEnforcementMode = lazy(() => import('./pages/AuthzEnforcementMode'));
const Connectors = lazy(() => import('./pages/Connectors'));
const GovernanceOverview = lazy(() => import('./pages/GovernanceOverview'));
const GovernanceImportBundle = lazy(() => import('./pages/GovernanceImportBundle'));
const GovernanceBundleHistory = lazy(() => import('./pages/GovernanceBundleHistory'));
const GovernanceBundleDetail = lazy(() => import('./pages/GovernanceBundleDetail'));
const GovernanceApiUserAssociations = lazy(() => import('./pages/GovernanceApiUserAssociations'));
const ApiAccessManagement = lazy(() => import('./pages/ApiAccessManagement'));
const GovernanceEffectiveAccess = lazy(() => import('./pages/GovernanceEffectiveAccess'));
const GovernanceAccessTrace = lazy(() => import('./pages/GovernanceAccessTrace'));

const PageFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center text-sm text-slate-500" role="status">
    Loading workspace…
  </div>
);

export default function App() {
  return (
    <BrowserRouter basename="/ui">
      <Suspense fallback={<PageFallback />}>
        <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<RequireAuth />}>
          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/overview" replace />} />
            <Route path="overview" element={<PlatformDashboard />} />
            {/* RAM-158: Dashboard.tsx/Blueprint.tsx are superseded by /overview
                (PlatformDashboard) -- both old routes now just redirect there. */}
            <Route path="dashboard" element={<Navigate to="/overview" replace />} />
            <Route path="connections" element={<Connections />} />
            <Route path="connections/new" element={<ConnectionWizard />} />
            <Route path="connections/api/new" element={<ApiConnectionWizard />} />
            <Route path="connections/api/:connectionId/workspace" element={<ApiConnectionWorkspace />} />
            <Route path="connections/platform/new" element={<DataPlatformWizard />} />
            <Route path="connections/files/new" element={<FileObjectWizard />} />
            <Route path="connections/streams/new" element={<StreamingWizard />} />
            <Route path="connections/catalogs/new" element={<CatalogConnectionWizard />} />
            <Route path="connections/mcp/new" element={<McpServerWizard />} />
            <Route path="connections/:connectionId/workspace" element={<ConnectionWorkspace />} />
            <Route path="drift" element={<Drift />} />
            <Route path="audit" element={<AuditTrail />} />
            <Route path="platform" element={<Platform />} />
            <Route path="settings" element={<Navigate to="/settings/environment" replace />} />
            <Route path="settings/environment" element={<EnvironmentManagement />} />
            <Route path="settings/license" element={<LicenseManagement />} />
            <Route path="settings/my-api-access" element={<MyApiAccess />} />
            <Route path="blueprint" element={<Navigate to="/overview" replace />} />
            <Route path="blueprint/packages" element={<Packages />} />
            <Route path="blueprint/packages/import" element={<ImportWizard />} />
            <Route path="blueprint/runtime-manager" element={<RuntimeManager />} />
            <Route path="blueprint/usage" element={<Usage />} />
            <Route path="blueprint/api-access/users" element={<ApiAccessUsers />} />
            <Route path="blueprint/api-access/roles" element={<ApiAccessRoles />} />
            <Route path="blueprint/api-access/resources" element={<AuthzResources />} />
            <Route path="blueprint/api-access/policies" element={<PolicyManagement />} />
            {/* RAM-242: PEP enforcement-mode status (read-only) -- RAM-243 extends this SAME
                page/component with override management, no separate route added for that. */}
            <Route path="blueprint/api-access/enforcement-mode" element={<AuthzEnforcementMode />} />
            {/* RAM-246: connector/enforcement-adapter catalog (read-only) -- RAM-247 extends this
                SAME page with creating/testing/provisioning bindings, no separate route planned. */}
            <Route path="blueprint/api-access/connectors" element={<Connectors />} />
            <Route path="blueprint/monitoring/activity" element={<AuthzActivity />} />
            {/* RAM-268 -- Control Plane -> Runtime Suite Governance Bundle import + PEP UI. */}
            <Route path="governance/overview" element={<GovernanceOverview />} />
            <Route path="governance/import" element={<GovernanceImportBundle />} />
            <Route path="governance/bundle-history" element={<GovernanceBundleHistory />} />
            <Route path="governance/bundles/:bundleId" element={<GovernanceBundleDetail />} />
            <Route path="governance/api-user-associations" element={<GovernanceApiUserAssociations />} />
            {/* The approved API Access redesign -- replaces the legacy identity pages in the NAV
                only; their routes above/below stay URL-reachable during the transition. */}
            <Route path="governance/api-access" element={<ApiAccessManagement />} />
            <Route path="governance/effective-access" element={<GovernanceEffectiveAccess />} />
            <Route path="governance/access-trace" element={<GovernanceAccessTrace />} />
          </Route>
        </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
